import cn from 'classnames';
import * as React from 'react';

import Text from '../text';
import useInputValidation from '../use-input-validation';

import { TextArea as Props, themes, sizes } from './text-area.types';

const RefTextArea = React.forwardRef<
  HTMLTextAreaElement,
  React.PropsWithChildren<Props>
>(
  (
    {
      children,
      helpText,
      label,
      name,
      placeholder,
      disabled,
      required,
      isLabelVisible = true,
      helpIcon,
      size = sizes.default,
      theme = themes.light,
      isShort,
      maxLength,
      defaultValue,
      ...restProps
    },
    ref,
  ) => {
    const [onChange, onInvalid, error] = useInputValidation();
    const [count, setCount] = React.useState(0);

    React.useEffect(() => {
      if (defaultValue) {
        if (typeof defaultValue === 'string') {
          setCount(defaultValue.length);
        } else if (typeof defaultValue === 'number') {
          setCount(defaultValue);
        }
      }
    }, [restProps]);

    return (
      <label className="text-area">
        <div className="text-area__wrapper">
          {isLabelVisible ? (
            <div className="text-area__header">
              <Text
                theme={Text.themes.emphasis}
                size={
                  size === sizes.default ? Text.sizes.basic : Text.sizes.small
                }
                className={cn('text-area__label', {
                  [`text-area__label--theme-${theme}-disabled`]: disabled,
                  [`text-area__label--theme-${theme}`]: theme,
                })}
              >
                {label}
                {required ? (
                  <span
                    className={cn('text-area__asterisk', {
                      [`text-area__asterisk--theme-${theme}-disabled`]:
                        disabled,
                      [`text-area__asterisk--theme-${theme}`]: theme,
                    })}
                  >
                    *
                  </span>
                ) : null}
                {children}
              </Text>
              {maxLength ? (
                <Text className="text-area__max" size={Text.sizes.small}>
                  {count}/{maxLength}
                </Text>
              ) : null}
            </div>
          ) : null}
          <textarea
            className={cn('text-area__input', {
              [`text-area__input--theme-${theme}`]: theme,
              [`text-area__input--theme-${theme}-error`]: error,
              [`text-area__input--size-${size}-short`]: isShort,
              [`text-area__input--size-${size}`]: size,
            })}
            aria-label={isLabelVisible ? label : undefined}
            name={name}
            maxLength={maxLength}
            ref={ref}
            cols={1}
            required={required}
            defaultValue={defaultValue}
            rows={isShort ? 1 : 2}
            onChange={e => {
              // NOTE: To not break compatibility
              if (restProps.onChange) {
                restProps.onChange(e);
              }
              onChange(e);
              if (maxLength) {
                setCount(e.target.value.length);
              }
            }}
            onInvalid={e => {
              // NOTE: To not break compatibility
              if (restProps.onInvalid) restProps.onInvalid(e);
              onInvalid(e);
            }}
            placeholder={placeholder}
            disabled={disabled}
            {...restProps}
          />
        </div>

        {helpText ? (
          <div className="text-area__help">
            <Text
              size={Text.sizes.interface}
              className={cn('text-area__help-text', {
                [`text-area__help-text--theme-${theme}-error`]: error,
                [`text-area__help-text--theme-${theme}`]: theme,
              })}
            >
              {helpIcon ? helpIcon : null}
              {helpText}
            </Text>
          </div>
        ) : null}

        {/* NOTE: This is aria-hidden because reporting of validation errors is handled by the browser */}
        {error && (
          <div aria-hidden="true" className="text-area__error">
            <Text>{error}</Text>
          </div>
        )}
      </label>
    );
  },
);

// NOTE: If this isn't set, stack traces say "forwardRef" instead of "TextArea"
RefTextArea.displayName = 'TextArea';
// NOTE: The component is redeclared here because TypeScript doesn't accept assigning extra properties to `RefComponent` when it's declared

const TextArea: typeof RefTextArea & {
  themes: typeof themes;
  sizes: typeof sizes;
} = Object.assign(RefTextArea, { themes, sizes });

export default TextArea;
